import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { reviewInvitationRepository } from "../repositories";
export const usePublicReviewStore = defineStore("publicReview", () => {
    // STATE
    const reviewContext = ref(null);
    const isLoadingReview = ref(false);
    const isSubmittingReview = ref(false);
    const reviewError = ref(null);
    const _pageStateOverride = ref(null);
    // GETTERS
    const pageState = computed(() => {
        if (isLoadingReview.value)
            return "loading";
        if (_pageStateOverride.value)
            return _pageStateOverride.value;
        if (!reviewContext.value)
            return "invalid";
        switch (reviewContext.value.status) {
            case "active":
                return "eligible";
            case "already_submitted":
                return "already_submitted";
            case "revoked":
                return "revoked";
            case "expired":
                return "expired";
            case "consumed":
                return "consumed";
            case "disabled":
                return "disabled";
            default:
                return "invalid";
        }
    });
    // ACTIONS
    async function loadReviewContext(invitationId, accessGrant) {
        if (!invitationId || !accessGrant) {
            _pageStateOverride.value = "invalid";
            return;
        }
        isLoadingReview.value = true;
        reviewError.value = null;
        _pageStateOverride.value = null;
        reviewContext.value = null;
        try {
            reviewContext.value = await reviewInvitationRepository.getContext(invitationId, accessGrant);
        }
        catch {
            _pageStateOverride.value = "invalid";
        }
        finally {
            isLoadingReview.value = false;
        }
    }
    async function submitReview(invitationId, accessGrant, data) {
        if (isSubmittingReview.value)
            return;
        isSubmittingReview.value = true;
        reviewError.value = null;
        try {
            await reviewInvitationRepository.submitReview(invitationId, accessGrant, data);
            _pageStateOverride.value = "success";
        }
        catch (err) {
            // Reload context so the correct state card renders (already_submitted/revoked/expired/
            // consumed/invalid). If the reload resolves to a non-eligible state the card explains what
            // happened; only keep the error message when the form is still the right thing to show.
            await loadReviewContext(invitationId, accessGrant);
            reviewError.value =
                pageState.value === "eligible"
                    ? err instanceof Error
                        ? err.message
                        : "Error submitting review"
                    : null;
            throw err;
        }
        finally {
            isSubmittingReview.value = false;
        }
    }
    function $reset() {
        reviewContext.value = null;
        isLoadingReview.value = false;
        isSubmittingReview.value = false;
        reviewError.value = null;
        _pageStateOverride.value = null;
    }
    return {
        reviewContext,
        isLoadingReview,
        isSubmittingReview,
        reviewError,
        pageState,
        loadReviewContext,
        submitReview,
        $reset,
    };
});
